<template>
  <div :slot-classes="{ left: 'left-0 flex justify-center md:sticky' }" stretch>
    <div class="heading-3 mb-12">
      {{ i18n(translations.resultTitle) }}
    </div>

    <div class="relative">
      <div class="border-static-info-mid rounded-sm relative border p-12">
        <div class="heading-3 mb-4">
          {{ product.titles.default }}
          <div
            v-if="selectedColorCode"
            aria-hidden
            class="rounded-full ml-8 inline-block size-[12px]"
            data-test="selected-color-code"
            :style="{ background: selectedColorCode }"
          />
        </div>

        <div
          v-if="selectedOptions"
          class="mt-6 flex flex-wrap gap-8"
          data-test="selected-options"
        >
          <RevTag
            v-for="option in selectedOptions"
            :key="option"
            :label="option"
            variant="secondary"
            variation="outline"
          />
        </div>

        <RevDivider class="my-20" />

        <div class="my-4 mr-8 flex items-center">
          <div v-if="product.images?.[0]" class="mr-8 flex justify-center">
            <RevIllustration
              :alt="product.images[0].description"
              :height="104"
              :src="product.images[0].url"
              :width="104"
            />
          </div>
          <div class="grow">
            <Price
              v-if="product && selectedOffer"
              :mobile-plan="selectedMobilePlan"
              :price
              :product
              :selectedOffer
            />
          </div>
        </div>
      </div>
      <div v-if="selectedOffer" class="my-16 flex items-center">
        <RevButton
          class="mood-tangaroa"
          data-id="product-page-buy-button-mobile"
          full-width="always"
          variant="primary"
          @click="onAddToCart"
        >
          {{ i18n(translations.addToCart) }}
        </RevButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// fixme: duplicated code due to Japan improved PP

import { computed } from 'vue'

import type { Price as PriceType } from '@backmarket/http-api'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevDivider } from '@ds/components/Divider'
import { RevIllustration } from '@ds/components/Illustration'
import { RevTag } from '@ds/components/Tag'

import { useAddToCart } from '~/scopes/product/composables/useAddToCart'
import { useProductTracking } from '~/scopes/product/composables/useProductTracking'
import type { Step } from '~/scopes/product/pages/Product-JP/components/CustomizationFunnel/utils/types'

import Price from '../Price/Price.vue'

import translations from './MobileAtc.translations'

const props = withDefaults(
  defineProps<{
    selectedOffer?: GetPickersResponse['selectedOffer'] | null
    product: GetProductResponse
    price: PriceType
    rating?: Rating | null
    tracking: ReturnType<typeof useProductTracking>
    selectedMobilePlan?: MobilePlanOffer
    funnelData?: Step[]
  }>(),
  {
    selectedOffer: null,
    rating: null,
    selectedMobilePlan: undefined,
    funnelData: undefined,
  },
)
const i18n = useI18n()

const selectedColorCode = computed(() => {
  const colorStep = props.funnelData?.find((step) => step.id === 'color')

  return (
    colorStep?.options?.find((option) => option.selected)?.color ?? undefined
  )
})

const selectedOptions = computed(() => {
  const variants = props.funnelData
    ?.reduce<Array<string>>((acc, { options, type }) => {
      // We don't want to display in the sticky bar or the proposal the following steps:
      // - services
      if (type !== 'service') {
        const label =
          options?.find(({ selected }) => !!selected)?.label ?? undefined
        if (label) {
          acc.push(typeof label === 'object' ? i18n(label) : label)
        }
      }

      return acc
    }, [])
    ?.filter(Boolean)

  return variants || []
})

const { addToCart } = useAddToCart()

function onAddToCart() {
  if (props.selectedOffer) {
    addToCart({
      listingId: props.selectedOffer.offerLegacyId,
      listingPublicId: props.selectedOffer.offerId,
      tracking: {
        ...props.tracking.product.value,
        list: 'product_page',
      },
      partnerPromoCodes: props.product.includedServiceOffers.partnerPromoCodes,
      isTradeInEligible: props.product.isTradeInEligible,
    })
  }
}
</script>
